import { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import FeedbackIcon from '@mui/icons-material/Feedback';

export default function FeedbackDialog({ open, onClose, prefillRecipeId }) {
    const [type, setType] = useState('suggestion');
    const [message, setMessage] = useState('');
    const includeContact = true;
    const [selectedRecipe, setSelectedRecipe] = useState(null);
    const [recipeOptions, setRecipeOptions] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');

    // Build recipe options from sessionStorage cache, fetching if not yet loaded
    useEffect(() => {
        if (!open) return;

        const buildOptions = (data) => {
            const options = Object.values(data.recipes || {}).flat()
                .filter(r => r.title)
                .map(r => ({ id: r.id, label: r.title }));
            options.sort((a, b) => a.label.localeCompare(b.label));
            setRecipeOptions(options);
        };

        const cached = JSON.parse(sessionStorage.getItem('recipeList'));
        if (cached?.recipes) {
            buildOptions(cached);
        } else {
            fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/recipes`, {
                headers: { Authorization: `Bearer ${Cookies.get('token')}` },
            })
                .then(r => r.json())
                .then(data => {
                    sessionStorage.setItem('recipeList', JSON.stringify(data));
                    buildOptions(data);
                })
                .catch(() => {});
        }
    }, [open]);

    // Auto-fill recipe when prefillRecipeId is provided
    useEffect(() => {
        if (!open || !prefillRecipeId || recipeOptions.length === 0) return;
        const match = recipeOptions.find(r => r.id === parseInt(prefillRecipeId, 10));
        if (match) setSelectedRecipe(match);
    }, [open, prefillRecipeId, recipeOptions]);

    const handleClose = () => {
        if (submitting) return;
        setType('suggestion');
        setMessage('');
        setSelectedRecipe(null);
        setSubmitted(false);
        setError('');
        onClose();
    };

    const handleSubmit = async () => {
        if (!message.trim()) {
            setError('Please enter a message before submitting.');
            return;
        }
        setError('');
        setSubmitting(true);
        try {
            const resp = await fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/feedback`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${Cookies.get('token')}`,
                },
                body: JSON.stringify({
                    type,
                    message,
                    include_contact: includeContact,
                    recipe_id: selectedRecipe?.id ?? null,
                    recipe_name: selectedRecipe?.label ?? null,
                    page: window.location.pathname,
                    environment: process.env.REACT_APP_BACKEND_ADDRESS?.includes('dev') ? 'dev' : 'prod',
                    user_agent: navigator.userAgent,
                }),
            });
            if (!resp.ok) throw new Error();
            setSubmitted(true);
        } catch {
            setError('Something went wrong. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FeedbackIcon color="primary" />
                Send Feedback
            </DialogTitle>

            {submitted ? (
                <>
                    <DialogContent>
                        <Box sx={{ textAlign: 'center', py: 3 }}>
                            <Typography variant="h6" gutterBottom>Thanks for your feedback!</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Your message has been submitted. Franco will take a look soon.
                            </Typography>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose} variant="contained">Close</Button>
                    </DialogActions>
                </>
            ) : (
                <>
                    <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2 }}>
                        <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                What kind of feedback is this?
                            </Typography>
                            <ToggleButtonGroup
                                value={type}
                                exclusive
                                onChange={(_, val) => { if (val) setType(val); }}
                                size="small"
                                fullWidth
                            >
                                <ToggleButton value="bug">
                                    <BugReportIcon fontSize="small" sx={{ mr: 0.75 }} />
                                    Bug
                                </ToggleButton>
                                <ToggleButton value="suggestion">
                                    <LightbulbIcon fontSize="small" sx={{ mr: 0.75 }} />
                                    Suggestion
                                </ToggleButton>
                                <ToggleButton value="other">
                                    <ChatBubbleOutlineIcon fontSize="small" sx={{ mr: 0.75 }} />
                                    Other
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Box>

                        <TextField
                            label="Message"
                            multiline
                            minRows={4}
                            fullWidth
                            value={message}
                            onChange={e => { setMessage(e.target.value); setError(''); }}
                            placeholder="Describe the issue or idea..."
                            error={!!error}
                            helperText={error}
                        />

                        <Autocomplete
                            options={recipeOptions}
                            value={selectedRecipe}
                            onChange={(_, val) => setSelectedRecipe(val)}
                            getOptionKey={(o) => o.id}
                            isOptionEqualToValue={(o, v) => o.id === v.id}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Attach a recipe (optional)"
                                    placeholder="Search by name..."
                                    size="small"
                                />
                            )}
                            clearOnEscape
                            noOptionsText="No recipes found"
                        />

                    </DialogContent>

                    <DialogActions>
                        <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
                        <Button
                            onClick={handleSubmit}
                            variant="contained"
                            disabled={submitting}
                            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                            {submitting ? 'Submitting...' : 'Submit'}
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
}
